import { MapboxOverlay } from '@deck.gl/mapbox'
import { useControl } from 'react-map-gl/maplibre'
import { useMemo, useEffect } from 'react'
import { BLUE, MAIN_MAP_ID, YELLOW } from '~/constants'
import { useAppStore, usePMTiles } from '~/hooks'
import { WaveArrowsLayer } from './WaveArrowsLayer'
import { AmbientLight, LightingEffect } from '@deck.gl/core'
import { interpolateRGB } from '~/util/color'
import { boundsForAttributeAndDepths } from '~/util/waves'

/** Brightens the extruded wave arrows. */
const wavesLightingEffect = new LightingEffect({
  ambientLight: new AmbientLight({
    color: [255, 255, 255],
    intensity: 2,
  }),
})

export function DeckGLOverlay() {
  const { layerInfo } = usePMTiles()
  const { dataset, wavesProperty, depths, setDeckRef } = useAppStore()

  const effects = useMemo(() => [wavesLightingEffect], [])

  const waveLayer = useMemo(() => {
    if (dataset !== 'waves' || !layerInfo) return null
    const WPsumBounds = boundsForAttributeAndDepths(depths, 'WPsum', layerInfo.waves)
    if (!WPsumBounds) return null
    const getColor = interpolateRGB(WPsumBounds.min, WPsumBounds.max, YELLOW, BLUE)
    return new WaveArrowsLayer({
      data: '',
      id: `WaveArrowsLayer-${wavesProperty}`,
      pmTiles: layerInfo.waves[0]!.pmTiles,
      wavesProperty,
      getColor,
      depths,
    })
  }, [dataset, layerInfo, wavesProperty, depths])

  const layers = useMemo(() => (waveLayer ? [waveLayer] : []), [waveLayer])

  const overlay = useControl<MapboxOverlay>(
    () =>
      new MapboxOverlay({
        id: MAIN_MAP_ID,
        layers,
        effects,
      })
  )

  useEffect(() => {
    if (overlay) {
      setDeckRef(overlay)
    }
    overlay.setProps({
      id: MAIN_MAP_ID,
      interleaved: true,
      layers,
      effects,
    })
  }, [overlay, layers, effects, setDeckRef])

  return null
}
